'use strict';

const _ = require('lodash');
const { differenceInDays } = require('date-fns');

const Enum = require('./enum');

/**
 * @const {number}
 * Maximum amount of slots on a
 * lock.
 */
const LOCK_SLOTS_MAX_NUM = 255;

/**
 * @const {number}
 * Minimum lock type compatible
 * with smart alert feature
 */
const SMART_ALERT_MIN_LOCK_TYPE = 3;

/**
 * @const {number}
 * Minimum lock type compatible
 * with temporal access keypad PINS
 * HELIOS clock cannot be trusted
 */
const TEMPORAL_KEYPAD_PIN_MIN_LOCK_TYPE = 2;

/**
 * @const {number}
 * Minimum lock type compatible
 * with smart alert feature
 */
const DOOR_STATE_MIN_LOCK_TYPE = 3;

/**
 * @const {number}
 * Minimum lock type compatible
 * with hyper bridge feature
 */
const HYPER_BRIDGE_MIN_LOCK_TYPE = 3;

/**
 * @const {number}
 * Default door state open timeout
 */
const DOOR_STATE_OPEN_TIMEOUT = 120;

/**
 * @const {string}
 * Door state change user id for log entries
 */
const DOOR_STATE_LOG_USER_ID = 'doorstatechange';

/**
 * @const {string}
 * Door state change first name for log entries
 */
const DOOR_STATE_LOG_USER_FIRST_NAME = 'Door State';

/**
 * @const {string}
 * Door state change last name for log entries
 */
const DOOR_STATE_LOG_USER_LAST_NAME = 'Change';

/**
 * @const {number}
 *
 * Door state log entries' timestamp's
 * are used as debounce scores; but when updates
 * have no timestamps (from POST /private/locks/status/:lockID)
 * we assign a low score so that update doesn't
 * interfere with updates from log entries
 * (in case they compete)
 */
const DOOR_STATE_UPDATE_NO_LOG_SCORE = 1;

/**
 * Defines lock actions.
 *
 * @enum
 * @readonly
 */
const LockActions = Object.create(Enum, {
  LOCK: { value: 'lock', enumerable: true },
  UNLOCK: { value: 'unlock', enumerable: true },
  UNLATCH: { value: 'unlatch', enumerable: true },
  SECURE: { value: 'secure', enumerable: true },
  ESCAPE_UNLOCK: { value: 'escapeUnlock', enumerable: true },
  CALIBRATE: { value: 'calibrate', enumerable: true },
  INVALID: { value: 'invalid', enumerable: true },
  toStatus: {
    value: function(lockAction) {
      switch (lockAction) {
        case this.LOCK:
          return LockStatuses.LOCKED;
        case this.CALIBRATE:
        case this.UNLOCK:
          return LockStatuses.UNLOCKED;
        case this.UNLATCH:
          return LockStatuses.UNLATCHED;
        case this.SECURE:
          return LockStatuses.SECURE_MODE;
        case this.ESCAPE_UNLOCK:
          return LockStatuses.UNLOCKED; // When the lock is unlocked through the escape button, the status should be unlock
        default:
          return LockStatuses.UNKNOWN;
      }
    },
    enumerable: false,
  },
});

/**
 * Defines lock credential actions.
 *
 * @enum
 * @readonly
 */
const LockCredentialActions = Object.create(Enum, {
  LOCK: { value: 'lock', enumerable: true },
  UNLOCK: { value: 'unlock', enumerable: true },
  SECURE: { value: 'secure', enumerable: true },
  INVALID: { value: 'invalidcode', enumerable: true },
  LOAD: { value: 'loadedcredential', enumerable: true },
  ENABLE: { value: 'enabledcredential', enumerable: true },
  DELETE: { value: 'removedcredential', enumerable: true },
  DISABLE: { value: 'disabledcredential', enumerable: true },
  UPDATE: { value: 'updatedcredential', enumerable: true },
});

/**
 * Defines tamper actions.
 *
 * @enum
 * @readonly
 */
const LockTamperedAction = Object.create(Enum, {
  TooManyAuthErrors: { value: 'TamperTooManyAuthErrors', enumerable: true },
  FrontRemoved: { value: 'TamperFrontRemoved', enumerable: true },
  HighTemperature: { value: 'TamperHighTemperature', enumerable: true },
  Clear: { value: 'TamperClear', enumerable: true },
  ForceOpen: { value: 'TamperDoorForcedOpen', enumerable: true },
  Unknown: { value: 'Unknown', enumerable: true },
  toRawActions: {
    value: function(lockTamperedAction) {
      switch (lockTamperedAction) {
        case this.TooManyAuthErrors:
          return 'kAugTamper_TooManyAuthErrors';
        case this.FrontRemoved:
          return 'kAugTamper_FrontRemoved';
        case this.HighTemperature:
          return 'kAugTamper_HighTemperature';
        case this.Clear:
          return 'kAugTamper_Clear';
        case this.ForceOpen:
          return 'kAugTamper_DoorForcedOpen';
        default:
          return 'kAugTamper_Unknown';
      }
    },
    enumerable: false,
  },
  toLockSetting: {
    value: function(lockTamperedAction) {
      switch (lockTamperedAction) {
        case this.TooManyAuthErrors:
          return 'tamperTooManyUnlockAttempts';
        case this.FrontRemoved:
          return 'tamperFrontFaceRemoved';
        case this.ForceOpen:
          return 'tamperDoorForcedOpen';
        default:
          return '';
      }
    },
    enumerable: false,
  },
});

/**
 * Defines raw tamper actions.
 *
 * @enum
 * @readonly
 */
const LockTamperedRawAction = Object.create(Enum, {
  TooManyAuthErrors: { value: 'TamperTooManyAuthErrors', enumerable: true },
  RAW_TooManyAuthErrors: { value: 'kaugtamper_toomanyautherrors', enumerable: true },
  FrontRemoved: { value: 'TamperFrontRemoved', enumerable: true },
  RAW_FrontRemoved: { value: 'kaugtamper_frontremoved', enumerable: true },
  HighTemperature: { value: 'TamperHighTemperature', enumerable: true },
  RAW_HighTemperature: { value: 'kaugtamper_hightemperature', enumerable: true },
  Clear: { value: 'TamperClear', enumerable: true },
  RAW_Clear: { value: 'kaugtamper_clear', enumerable: true },
  ForceOpen: { value: 'TamperDoorForcedOpen', enumerable: true },
  RAW_ForceOpen: { value: 'kaugtamper_doorforcedopen', enumerable: true },
  Unknown: { value: 'Unknown', enumerable: true },
  RAW_Unknown: { value: 'kaugtamper_unknown', enumerable: true },
  toStatus: {
    value: function(rawLockTamperedAction) {
      switch (rawLockTamperedAction.toLowerCase()) {
        case this.RAW_TooManyAuthErrors:
          return LockTamperedAction.TooManyAuthErrors;
        case this.RAW_FrontRemoved:
          return LockTamperedAction.FrontRemoved;
        case this.RAW_HighTemperature:
          return LockTamperedAction.HighTemperature;
        case this.RAW_Clear:
          return LockTamperedAction.Clear;
        case this.RAW_ForceOpen:
          return LockTamperedAction.ForceOpen;
        case this.RAW_UNKNOWN:
          return LockTamperedAction.Unknown;
        default:
          return LockTamperedAction.Unknown;
      }
    },
    enumerable: false,
  },
});

/**
 * Defines raw lock statuses.
 *
 * @enum
 * @readonly
 */
const LockRawStatuses = Object.create(Enum, {
  LOCKED: { value: 'locked', enumerable: true },
  RAW_LOCKED: { value: 'kauglockstate_locked', enumerable: true },
  UNLOCKED: { value: 'unlocked', enumerable: true },
  RAW_UNLOCKED: { value: 'kauglockstate_unlocked', enumerable: true },
  UNLATCHED: { value: 'unlatched', enumerable: true },
  RAW_UNLATCHED: { value: 'kauglockstate_unlatched', enumerable: true },
  SECURE_MODE: { value: 'securemode', enumerable: true },
  RAW_SECURE_MODE: { value: 'kauglockstate_securemode', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },
  RAW_UNKNOWN: { value: 'kauglockstate_unknownstaticposition', enumerable: true },
  NOT_ADVERTISING: { value: 'not-advertising', enumerable: true },
  toStatus: {
    value: function(rawLockStatus) {
      if (typeof rawLockStatus !== 'string') {
        return LockStatuses.INVALID;
      }

      switch (rawLockStatus.toLowerCase()) {
        case this.LOCKED:
        case this.RAW_LOCKED:
          return LockStatuses.LOCKED;
        case this.UNLOCKED:
        case this.RAW_UNLOCKED:
          return LockStatuses.UNLOCKED;
        case this.UNLATCHED:
        case this.RAW_UNLATCHED:
          return LockStatuses.UNLATCHED;
        case this.SECURE_MODE:
        case this.RAW_SECURE_MODE:
          return LockStatuses.SECURE_MODE;
        default:
          return LockStatuses.UNKNOWN;
      }
    },
    enumerable: false,
  },
  toAction: {
    value: function(rawLockStatus) {
      if (typeof rawLockStatus !== 'string') {
        return LockActions.INVALID;
      }

      switch (rawLockStatus.toLowerCase()) {
        case this.LOCKED:
        case this.RAW_LOCKED:
          return LockActions.LOCK;
        case this.UNLOCKED:
        case this.RAW_UNLOCKED:
          return LockActions.UNLOCK;
        case this.UNLATCHED:
        case this.RAW_UNLATCHED:
          return LockActions.UNLATCH;
        case this.SECURE_MODE:
        case this.RAW_SECURE_MODE:
          return LockActions.SECURE;
      }
    },
    enumerable: false,
  },
});

/**
 * Defines raw door state statuses.
 *
 * @enum
 * @readonly
 */
const LockDoorStateRawStatuses = Object.create(Enum, {
  INIT: { value: 'init', enumerable: true },
  RAW_INIT: { value: 'kaugdoorstate_init', enumerable: true },
  CLOSED: { value: 'closed', enumerable: true },
  RAW_CLOSED: { value: 'kaugdoorstate_closed', enumerable: true },
  AJAR: { value: 'ajar', enumerable: true },
  RAW_AJAR: { value: 'kaugdoorstate_ajar', enumerable: true },
  OPEN: { value: 'open', enumerable: true },
  RAW_OPEN: { value: 'kaugdoorstate_open', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },
  RAW_UNKNOWN: { value: 'kaugdoorstate_unknown', enumerable: true },
  toStatus: {
    value: function(rawLockDoorStateStatus) {
      if (typeof rawLockDoorStateStatus !== 'string') {
        return LockDoorStateStatuses.INVALID;
      }

      switch (rawLockDoorStateStatus.toLowerCase()) {
        case this.INIT:
        case this.RAW_INIT:
          return LockDoorStateStatuses.INIT;
        case this.CLOSED:
        case this.RAW_CLOSED:
          return LockDoorStateStatuses.CLOSED;
        case this.AJAR:
        case this.RAW_AJAR:
          return LockDoorStateStatuses.AJAR;
        case this.OPEN:
        case this.RAW_OPEN:
          return LockDoorStateStatuses.OPEN;
        case this.UNKNOWN:
        case this.RAW_UNKNOWN:
          return LockDoorStateStatuses.UNKNOWN;
        default:
          return LockDoorStateStatuses.UNKNOWN;
      }
    },
    enumerable: false,
  },
});

/**
 * Defines lock statuses.
 *
 * @enum
 * @readonly
 */
const LockStatuses = Object.create(Enum, {
  LOCKED: { value: 'locked', enumerable: true },
  UNLOCKED: { value: 'unlocked', enumerable: true },
  LOCKING: { value: 'locking', enumerable: true },
  UNLOCKING: { value: 'unlocking', enumerable: true },
  UNLATCHED: { value: 'unlatched', enumerable: true },
  UNLATCHING: { value: 'unlatching', enumerable: true },
  SECURE_MODE: { value: 'securemode', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },
  UNKNOWN_ERR_CONNECT: { value: 'unknown_error_during_connect', enumerable: true },
  UNKNOWN_ERR_STATUS: { value: 'unknown_error_during_status', enumerable: true },
  NOT_ADVERTISING: { value: 'not-advertising', enumerable: true },
  INVALID: { value: 'invalid', enumerable: true },
  toRawStatus: {
    value: function(lockStatus) {
      if (typeof lockStatus !== 'string') {
        return this.INVALID;
      }

      // ATM used for fast bridge return
      // value; needs camelCase style, otherwise
      // client doesn't understand
      switch (lockStatus) {
        case this.LOCKED:
          return 'kAugLockState_Locked';
        case this.UNLOCKED:
          return 'kAugLockState_Unlocked';
        case this.LOCKING:
          return 'kAugLockState_Locking';
        case this.UNLOCKING:
          return 'kAugLockState_Unlocking';
        case this.UNLATCHED:
          return 'kAugLockState_Unlatched';
        case this.UNLATCHING:
          return 'kAugLockState_Unlatching';
        case this.SECURE_MODE:
          return 'kAugLockState_SecureMode';
        default:
          return 'kAugLockState_UnknownStaticPosition';
      }
    },
    enumerable: false,
  },
  toAction: {
    value: function(lockStatus) {
      switch (lockStatus) {
        case this.LOCKED:
          return LockActions.LOCK;
        case this.UNLOCKED:
          return LockActions.UNLOCK;
        case this.UNLATCHED:
          return LockActions.UNLATCH;
        case this.SECURE_MODE:
          return LockActions.SECURE;
      }
    },
    enumerable: false,
  },
});

const LockDoorStateStatuses = Object.create(Enum, {
  INIT: { value: 'init', enumerable: true },
  CLOSED: { value: 'closed', enumerable: true },
  AJAR: { value: 'ajar', enumerable: true },
  OPEN: { value: 'open', enumerable: true },
  UNKNOWN: { value: 'unknown', enumerable: true },
  toRawStatus: {
    value: function(lockStatus) {
      if (typeof lockStatus !== 'string') {
        return this.INVALID;
      }

      // ATM used for fast bridge return
      // value; needs camelCase style, otherwise
      // client doesn't understand
      switch (lockStatus) {
        case this.INIT:
          return 'kAugDoorState_Init';
        case this.CLOSED:
          return 'kAugDoorState_Closed';
        case this.AJAR:
          return 'kAugDoorState_Ajar';
        case this.OPEN:
          return 'kAugDoorState_Open';
        case this.UNKNOWN:
          return 'kAugDoorState_Unknown';
        default:
          return 'kAugDoorState_Unknown';
      }
    },
    enumerable: false,
  },
  toAction: {
    value: function(lockDoorStateStatus) {
      switch (lockDoorStateStatus) {
        case this.OPEN:
          return 'dooropen';
        case this.CLOSED:
          return 'doorclosed';
        default:
          return 'unknown';
      }
    },
    enumerable: false,
  },
  parse: {
    value: function(lockDoorStateStatus) {
      if (typeof lockDoorStateStatus === 'string') {
        return lockDoorStateStatus.toLowerCase();
      }

      if (typeof lockDoorStateStatus !== 'number') {
        return this.UNKNOWN;
      }

      switch (lockDoorStateStatus) {
        case 0:
          return this.INIT;
        case 1:
          return this.CLOSED;
        case 2:
          return this.AJAR;
        case 3:
          return this.OPEN;
        default:
          return this.UNKNOWN;
      }
    },
    enumerable: false,
  },
  toNumber: {
    value: function(lockDoorStateStatus) {
      switch (lockDoorStateStatus) {
        case this.INIT:
          return 0;
        case this.CLOSED:
          return 1;
        case this.AJAR:
          return 2;
        case this.OPEN:
          return 3;
        default:
          return 4;
      }
    },
    enumerable: false,
  },
});

/**
 * Defines reserved slots on the lock.
 *
 * @enum
 * @readonly
 */
const LockReservedSlots = Object.create(Enum, {
  KEYPAD: { value: 255, enumerable: true },
  NOT_SUPER_USER: { value: 254, enumerable: true },
  HOME_KIT_KEY_MIN: { value: 256, enumerable: true },
  HOME_KIT_KEY_MAX: { value: 511, enumerable: true },
});

/**
 * Defines identifiers for lock firmware chips.
 *
 * @see https://augusthome.atlassian.net/wiki/spaces/ST/pages/108969486/OTA+Update+Specifications#OTAUpdateSpecifications-Lockfirmwarechipconstants
 * @enum
 * @readonly
 */
module.exports.LockFirmwareChips = Object.create(Enum, {
  ARM: { value: 'arm', enumerable: true },
  TI: { value: 'ti', enumerable: true },
  ST: { value: 'st', enumerable: true },
  BCM: { value: 'bcm', enumerable: true },
  DLG: { value: 'dlg', enumerable: true },
  SD: { value: 'sd', enumerable: true },
  YALE: { value: 'yale', enumerable: true },
  NORDIC: { value: 'nordic', enumerable: true },
  PKG: { value: 'pkg', enumerable: true },
  ARMGITHASH: { value: 'armGithash', enumerable: true },
  getLockChips: {
    value: function() {
      return {
        [LockVersions.V1]: [this.ARM, this.TI],
        [LockVersions.V2]: [this.BCM, this.ST],
        [LockVersions.V3]: [this.DLG, this.SD],
        [LockVersions.V4]: [this.DLG],
        [LockVersions.V5]: [this.PKG],
        [LockVersions.V6]: [this.DLG],
        [LockVersions.V7]: [this.DLG, this.YALE],
        [LockVersions.V1001]: [this.DLG, this.YALE],
        [LockVersions.V1002]: [this.NORDIC, this.YALE],
        [LockVersions.V1003]: [this.DLG, this.YALE],
        [LockVersions.V1005]: [this.PKG],
      };
    },
    enumerable: false,
  },
  /**
   * Transforms an object representing a lock's chips and versions to a string. Intended to mirror what mobile sends as
   * the :version parameter when calling GET /locks/:lockID/firmware/:version.
   *
   * @param {object} versionObject Object containing chips and firmware versions keyed by chip name, for example { sd: 1.0, dlg: 2.0 }
   * @param {constants.LockVersions} lockVersion -
   * @return {string} -
   */
  objectToLegacyString: {
    value: function(versionObject, lockVersion, options = {}) {
      if (!versionObject) {
        throw new Error(`Invalid versionObject of type: ${typeof versionObject}`);
      }

      const chipSets = _.get(options, 'chipSets');

      if (chipSets) {
        return `${versionObject[chipSets.tertiary]}-${versionObject[chipSets.secondary]}-${
          versionObject[chipSets.primary]
        }`;
      }

      switch (lockVersion) {
        case LockVersions.V1: {
          return `${_.get(versionObject, this.ARMGITHASH)}-${_.get(versionObject, this.ARM)}-${_.get(
            versionObject,
            this.TI
          )}`;
        }

        case LockVersions.V2:
          return `undefined-${_.get(versionObject, this.ST)}-${_.get(versionObject, this.BCM)}`;

        case LockVersions.V3:
          return `undefined-${_.get(versionObject, this.SD)}-${_.get(versionObject, this.DLG)}`;

        case LockVersions.V4:
        case LockVersions.V6:
          return `undefined-undefined-${_.get(versionObject, this.DLG)}`;

        // titan should not need to convert to the legacy string.
        // it is done here due to validations done on the legacy string
        // see server/middleware/locks.js::checkVersionString
        case LockVersions.V5:
          return `undefined-undefined-${_.get(versionObject, this.PKG)}`;

        case LockVersions.V7:
        case LockVersions.V1001:
        case LockVersions.V1003:
          return `undefined-${_.get(versionObject, this.YALE)}-${_.get(versionObject, this.DLG)}`;

        case LockVersions.V1002:
          return `undefined-${_.get(versionObject, this.YALE)}-${_.get(versionObject, this.NORDIC)}`;

        case LockVersions.V1005:
          return `undefined-undefined-${_.get(versionObject, this.PKG)}`;

        default:
          throw new Error(`Invalid lock version: ${lockVersion}`);
      }
    },
    enumerable: false,
  },

  /**
   * Parses concatenated version string, e.g. abcd1234-1.3.4-2.3.4 as provided
   * by clients when checking for firmware updates; can also parse version
   * strings as stored in db, e.g. abcd1234-1:3:4-2:3:4.
   *
   * @param {string} versionStr -
   * @param {constants.LockVersions} lockVersion -
   * @param {object} [options] -
   * @param {boolean} [options.useDefaultForUnknown] - if set, will assign '0.0.0' as a default
   * @return {Object} depending on lock version, e.g. {
   *  st: '1.3.4',
   *  bcm: '2.3.4'
   * }
   */
  legacyStringToObject: {
    value: function(versionStr, lockVersion, options = {}) {
      /**
       * Parses version string; sets undefined if falsy or
       * value is 'undefined.'
       *
       * @param {string} [version] -
       * @param {object} [options] -
       * @param {boolean} [options.useDefaultForUnknown] -
       * @return {string|undefined} -
       */
      function _parseVersion(version, options = {}) {
        options = options || {};

        if (!version || version.toLowerCase() === 'undefined') {
          if (_.get(options, 'useDefaultForUnknown')) {
            return '0.0.0';
          }

          return;
        }

        return version;
      }

      if (!this.isLegacyVersion(versionStr)) {
        throw new Error(`Invalid versionStr: ${versionStr}`);
      }

      const versions = versionStr.replace(/:/g, '.').split('-');
      const chipSets = _.get(options, 'chipSets');
      let firmwareObj = {};

      if (chipSets) {
        firmwareObj = {
          [chipSets.primary]: versions[2],
          [chipSets.secondary]: versions[1],
          [chipSets.tertiary]: versions[0],
        };
        delete firmwareObj.undefined;

        return _.omitBy(firmwareObj, value => !value || value === 'undefined');
      }

      switch (lockVersion) {
        case LockVersions.V1:
          firmwareObj = {
            [this.ARMGITHASH]: versions[0],
            [this.ARM]: _parseVersion(versions[1], options),
            [this.TI]: _parseVersion(versions[2], options),
          };

          break;
        case LockVersions.V2:
          firmwareObj = {
            [this.ST]: _parseVersion(versions[1], options),
            [this.BCM]: _parseVersion(versions[2], options),
          };

          break;
        case LockVersions.V3:
          firmwareObj = {
            [this.SD]: _parseVersion(versions[1], options),
            [this.DLG]: _parseVersion(versions[2], options),
          };

          break;
        case LockVersions.V4:
        case LockVersions.V6:
          firmwareObj = {
            [this.DLG]: _parseVersion(versions[2], options),
          };

          break;
        case LockVersions.V5:
        case LockVersions.V1005:
          firmwareObj = {
            [this.PKG]: _parseVersion(versions[2], options),
          };

          break;
        case LockVersions.V7:
        case LockVersions.V1001:
        case LockVersions.V1003:
          firmwareObj = {
            [this.YALE]: _parseVersion(versions[1], options),
            [this.DLG]: _parseVersion(versions[2], options),
          };

          break;
        case LockVersions.V1002:
          firmwareObj = {
            [this.YALE]: _parseVersion(versions[1], options),
            [this.NORDIC]: _parseVersion(versions[2], options),
          };

          break;
        default:
          throw new Error(`Parsing function does not support lock version ${lockVersion}`);
      }

      // remove undefined values
      return _.omitBy(firmwareObj, _.isUndefined);
    },
    enumerable: false,
  },

  /**
   * Checks if string contains legacy representation of a lock's firmware versions, e.g.:
   *
   * - -undefined-1.2.0
   * - undefined-undefined-1.2.0
   * - a7912d3-1.2.0-1.3.0
   * - etc.
   *
   * @param {string} version -
   * @return {boolean} -
   */
  isLegacyVersion: {
    value: function(version) {
      return (
        typeof version === 'string' &&
        (/[a-z0-9]{0,8}(-.)*/.test(version) || // L1 - git hash optionally followed by ARM and TI versions
          /undefined/.test(version)) // L2+ version string
      );
    },
    enumerable: false,
  },
});

/**
 * Defines identifiers to distinguish between
 * lock versions.
 *
 * @enum
 * @readonly
 */
const LockVersions = (module.exports.LockVersions = Object.create(Enum, {
  V1: { value: 'lock_version_1', enumerable: true }, // Helios
  V2: { value: 'lock_version_2', enumerable: true }, // Jupiter
  V3: { value: 'lock_version_3', enumerable: true }, // Europa
  V4: { value: 'lock_version_4', enumerable: true }, // Callisto
  V5: { value: 'lock_version_5', enumerable: true }, // Titan
  V6: { value: 'lock_version_6', enumerable: true }, // Terra
  V7: { value: 'lock_version_7', enumerable: true }, // Allegro
  V1001: { value: 'lock_version_1001', enumerable: true }, // Unity
  V1002: { value: 'lock_version_1002', enumerable: true }, // L-Unity Nordic / LUnity
  V1003: { value: 'lock_version_1003', enumerable: true }, // L-Unity Dialog
  V1005: { value: 'lock_version_1005', enumerable: true }, // Matrix reloaded prototype
  UNKNOWN: { value: 'unknown', enumerable: true },
  parseType: {
    value: function(lockType) {
      switch (lockType) {
        case 1:
          return this.V1;
        case 2:
          return this.V2;
        case 3:
          return this.V3;
        case 4:
          return this.V4;
        case 5:
          return this.V5;
        case 6:
          return this.V6;
        case 7:
          return this.V7;
        case 1001:
          return this.V1001;
        case 1002:
          return this.V1002;
        case 1003:
          return this.V1003;
        case 1005:
          return this.V1005;
        default:
          return this.UNKNOWN;
      }
    },
    enumerable: false,
  },
  toType: {
    value: function(lockVersion) {
      switch (lockVersion) {
        case this.V1:
          return 1;
        case this.V2:
          return 2;
        case this.V3:
          return 3;
        case this.V4:
          return 4;
        case this.V5:
          return 5;
        case this.V6:
          return 6;
        case this.V7:
          return 7;
        case this.V1001:
          return 1001;
        case this.V1002:
          return 1002;
        case this.V1003:
          return 1003;
        case this.V1005:
          return 1005;
        default:
          return -1; // unknown
      }
    },
    enumerable: false,
  },
  getSerialNumberRegex: {
    value: function(lockVersion) {
      switch (lockVersion) {
        case this.V1:
          return /^L1/;
        case this.V2:
          return /^L2/;
        case this.V3:
          return /^L3/;
        case this.V4:
          return /^L4/;
        case this.V5:
          return /^L5/;
        case this.V6:
          return /^L6/;
        case this.V7:
          return /^L7/;
        case this.V1001:
          return /^M1/;
        case this.V1002:
          return /^M4/;
        case this.V1003:
          return /^M2/;
        case this.V1005:
          return /^M5/;
        default:
          throw new Error(`Invalid lock version '${lockVersion}'.`);
      }
    },
    enumerable: false,
  },
  isSmartAlertEnabled: {
    value: function(lock) {
      const lockVersion = this.getVersionFromLockObject(lock);

      return this.toType(lockVersion) >= SMART_ALERT_MIN_LOCK_TYPE;
    },
    enumerable: false,
  },
  isDoorStateSupported: {
    value: function(lock) {
      const lockVersion = this.getVersionFromLockObject(lock);

      return this.toType(lockVersion) >= DOOR_STATE_MIN_LOCK_TYPE;
    },
    enumerable: false,
  },
  isHyperBridgeSupported: {
    value: function(lock) {
      const lockType = lock.Type;

      return lockType >= HYPER_BRIDGE_MIN_LOCK_TYPE;
    },
    enumerable: false,
  },
  hasBuiltInKeypad: {
    value: function(lock) {
      const lockVersion = this.getVersionFromLockObject(lock);

      return [LockVersions.V1001, LockVersions.V1002, LockVersions.V1003, LockVersions.V7].includes(lockVersion);
    },
  },
  isHomeKitSupported: {
    value: function(lock) {
      const lockVersion = this.getVersionFromLockObject(lock);

      return HOMEKIT_LOCK_TYPES.includes(lockVersion);
    },
  },
  isTemporalKeypadPinSupported: {
    value: function(lock) {
      const lockVersion = this.getVersionFromLockObject(lock);

      return this.toType(lockVersion) >= TEMPORAL_KEYPAD_PIN_MIN_LOCK_TYPE;
    },
  },
  hasClientCertificate: {
    value: function(lock) {
      const lockVersion = this.getVersionFromLockObject(lock);

      switch (lockVersion) {
        case LockVersions.V5:
        case LockVersions.V1005:
          return true;
        default:
          return false;
      }
    },
  },
  assertLockVersionString: {
    value: function(lockVersion) {
      if (typeof lockVersion !== 'string') {
        throw new TypeError(`Invalid lock version ${lockVersion}`);
      }

      const [lockString, versionString, versionNumberString] = lockVersion.split('_');
      const isValidLockVersion = lockString === 'lock' && versionString === 'version' && !isNaN(versionNumberString);
      if (!isValidLockVersion) {
        throw new Error(`Invalid lock version ${lockVersion}`);
      }
    },
  },
  /**
   * @param {Object} lock - A lock from the locks collection.
   * @param {boolean} defaultToV1 - If true, unknown locks return V1, otherwise UNKNOWN.
   * @returns {string} The lock version.
   */
  getVersionFromLockObject: {
    value: function(lock, defaultToV1 = true) {
      const type = Number(lock.Type);

      if (isNaN(type)) {
        // No type; try to check
        // serial number or MAC address;
        // JUPITER (Type 2) locks serials begin with 'L2...'
        // and MAC addresses start with '78:9c:85'.
        const V1_PREFIX_SERIAL = 'l1';
        const V2_PREFIX_SERIAL = 'l2';
        const V3_PREFIX_SERIAL = 'l3';
        const V4_PREFIX_SERIAL = 'l4';
        const V5_PREFIX_SERIAL = 'l5';
        const V6_PREFIX_SERIAL = 'l6';
        const V7_PREFIX_SERIAL = 'l7';
        const V1001_PREFIX_SERIAL = 'm1';
        const V1002_PREFIX_SERIAL = 'm4';
        const V1003_PREFIX_SERIAL = 'm2';
        const V1005_PREFIX_SERIAL = 'm5';
        const V2_PREFIX_MAC_ADDRESS = '78:9c:85';
        const serialNumber = (lock.serialNumber || '').toLowerCase();
        const serialNumberPrefix = serialNumber.substring(0, 2);
        const macAddress = (lock.macAddress || '').toLowerCase();
        const isType2 = serialNumberPrefix === V2_PREFIX_SERIAL || macAddress.substring(0, 7) === V2_PREFIX_MAC_ADDRESS;

        if (isType2) {
          return this.V2;
        } else if (serialNumberPrefix === V3_PREFIX_SERIAL) {
          return this.V3;
        } else if (serialNumberPrefix === V4_PREFIX_SERIAL) {
          return this.V4;
        } else if (serialNumberPrefix === V5_PREFIX_SERIAL) {
          return this.V5;
        } else if (serialNumberPrefix === V6_PREFIX_SERIAL) {
          return this.V6;
        } else if (serialNumberPrefix === V7_PREFIX_SERIAL) {
          return this.V7;
        } else if (serialNumberPrefix === V1001_PREFIX_SERIAL) {
          return this.V1001;
        } else if (serialNumberPrefix === V1002_PREFIX_SERIAL) {
          return this.V1002;
        } else if (serialNumberPrefix === V1003_PREFIX_SERIAL) {
          return this.V1003;
        } else if (serialNumberPrefix === V1005_PREFIX_SERIAL) {
          return this.V1005;
        } else if (serialNumberPrefix === V1_PREFIX_SERIAL || defaultToV1) {
          return this.V1;
        }

        return this.UNKNOWN;
      }

      return this.parseType(type);
    },
    enumerable: false,
  },
  serialToPrefix: {
    value: function(serialNumber) {
      const isValid = typeof serialNumber === 'string' && serialNumber.length >= 2;
      if (!isValid) {
        throw new TypeError(`Invalid serial number ${serialNumber}`);
      }

      return serialNumber.slice(0, 2).toLowerCase();
    },
    enumerable: false,
  },
  typeToVersionString: {
    value: function(lockType) {
      const isValid = typeof lockType === 'number' && !isNaN(lockType);
      if (!isValid) {
        throw new TypeError(`Invalid lock type ${lockType}`);
      }

      return `lock_version_${lockType}`;
    },
    enumerable: false,
  },
}));

/**
 * @const {number}
 * Lock types compatible with HomeKit.
 */
const HOMEKIT_LOCK_TYPES = [
  LockVersions.V2,
  LockVersions.V3,
  LockVersions.V5,
  LockVersions.V6,
  LockVersions.V1001,
  LockVersions.V1002,
  LockVersions.V1003,
  LockVersions.V1005,
];

const LockBatteryWarningStates = Object.create(Enum, {
  NONE: { value: 'lock_state_battery_warning_none', enumerable: true },
  TWO_WEEK: { value: 'lock_state_battery_warning_2week', enumerable: true },
  ONE_WEEK: { value: 'lock_state_battery_warning_1week', enumerable: true },
  TWO_DAY: { value: 'lock_state_battery_warning_2day', enumerable: true },
  getWarningState: {
    value: function(currentDate, batteryDeathDate) {
      const daysUntilBatteryDead = differenceInDays(batteryDeathDate, currentDate);

      switch (true) {
        case daysUntilBatteryDead < 0:
          throw new Error('The `batteryDeathDate` must be after the `currentDate`!');
        case daysUntilBatteryDead <= 2:
          return LockBatteryWarningStates.TWO_DAY;
        case daysUntilBatteryDead <= 7:
          return LockBatteryWarningStates.ONE_WEEK;
        case daysUntilBatteryDead <= 14:
          return LockBatteryWarningStates.TWO_WEEK;
        default:
          return LockBatteryWarningStates.NONE;
      }
    },
    enumerable: false,
  },
  getDaysBetweenNotifications: {
    value: function(level) {
      switch (level) {
        case LockBatteryWarningStates.TWO_DAY:
          return 1;
        case LockBatteryWarningStates.ONE_WEEK:
          return 1;
        case LockBatteryWarningStates.TWO_WEEK:
          return 2;
        default:
          throw new Error('Invalid LockBatteryWarningState');
      }
    },
    enumerable: false,
  },
});

const LockBatteryAlgorithmNames = Object.create(Enum, {
  VOLTAGE_7_FACTORS: { value: 'voltage7Factors', enumerable: true },
  VOLTAGE_7_FACTORS_LAST_VOLTAGE: { value: 'voltage7FactorsLastVoltage', enumerable: true },
  SIMPLIFIED_CR_123: { value: 'simplified-CR123', enumerable: true },
  SIMPLIFIED_CR_123_LAST_VOLTAGE: { value: 'simplified-CR123LastVoltage', enumerable: true },
  CUMULATIVE_AVERAGE_CR123: { value: 'cumulativeAverageCR123', enumerable: true },
});

/**
 * How long we think 4 AA batteries will last in each lock generation,
 * measured in milliseconds.
 */
const LockBatteryLifeEstimatesInMS = Object.create(Enum, {});
Object.defineProperty(LockBatteryLifeEstimatesInMS, LockVersions.V1, {
  value: 1000 * 60 * 60 * 24 * 30 * 6, // ~6 months
  enumerable: true,
});
Object.defineProperty(LockBatteryLifeEstimatesInMS, LockVersions.V2, {
  value: 1000 * 60 * 60 * 24 * 30 * 3, // ~3 months
  enumerable: true,
});
Object.defineProperty(LockBatteryLifeEstimatesInMS, LockVersions.V3, {
  value: 1000 * 60 * 60 * 24 * 30 * 12, // ~12 months
  enumerable: true,
});
Object.defineProperty(LockBatteryLifeEstimatesInMS, LockVersions.V4, {
  value: 1000 * 60 * 60 * 24 * 30 * 15, // ~15 months
  enumerable: true,
});
//TODO: add lock battery estimates time for L5
Object.defineProperty(LockBatteryLifeEstimatesInMS, LockVersions.V1001, {
  value: 1000 * 60 * 60 * 24 * 30 * 11, // ~11 months
  enumerable: true,
});

module.exports.LockFeatures = Object.freeze(
  Object.create(Enum, {
    supportsPinSlots: {
      value: lock => {
        switch (LockVersions.getVersionFromLockObject(lock)) {
          case LockVersions.V1001:
            return true;

          default:
            return false;
        }
      },
      enumerable: false,
    },
  })
);

module.exports.LockNotificationTypes = Object.create(Enum, {
  OPERATION: { value: 'operation', enumerable: true },
  OPERATION_KEYPAD_UNLOCK: { value: 'operation.keypad.unlock', enumerable: true },
  OPERATION_KEYPAD_LOCK: { value: 'operation.keypad.lock', enumerable: true },
  OPERATION_APP_UNLOCK: { value: 'operation.app.unlock', enumerable: true },
  OPERATION_APP_LOCK: { value: 'operation.app.lock', enumerable: true },
  SYSTEM_STATUS: { value: 'systemstatus', enumerable: true },
  BATTERY: { value: 'battery', enumerable: true },
  ACCESS_MGMT: { value: 'accessmgmt', enumerable: true },
  CONFIGURATION: { value: 'configuration', enumerable: true },
});

module.exports.ESCAPE_LOG_ACTIONS = {
  name: 'EscapeUnlock',
  type: 'LockOperation',
  code: 'EscapeUnlock',
  slot: 'keySlot',
};

module.exports.LockAccessoryEventAction = Object.create(Enum, {
  BUTTON_PRESS: { value: 'AccessoryEventButtonPress', enumerable: true },
  MOTION_DETECT: { value: 'AccessoryEventMotionDetect', enumerable: true },

  toLockSetting: {
    value: function(LockAccessoryEventAction) {
      switch (LockAccessoryEventAction) {
        case this.BUTTON_PRESS:
          return 'accessoryEventButtonPress';
        case this.MOTION_DETECT:
          return 'accessoryEventMotionDetect';
        default:
          return '';
      }
    },
    enumerable: false,
  },
});

exports.LOCK_STATE_BATTERY_WARNING_NONE = 'lock_state_battery_warning_none';
exports.LOCK_STATE_BATTERY_WARNING_WARNING = 'lock_state_battery_warning_warning';
exports.LOCK_STATE_BATTERY_WARNING_CRITICAL = 'lock_state_battery_warning_critical';
exports.LOCK_BATTERY_SD = 1.25;
exports.LOCK_BATTERY_NEW_VOLTAGE = 6000;
exports.LOCK_BATTERY_USED_VOLTAGE = 5500;
exports.LOCK_BATTERY_MIN_VOLTAGE = 2500;
exports.LOCK_BATTERY_MAX_VOLTAGE = 7000;
exports.LOCK_SLOTS_MAX_NUM = LOCK_SLOTS_MAX_NUM;
exports.SMART_ALERT_MIN_LOCK_TYPE = SMART_ALERT_MIN_LOCK_TYPE;
exports.LockActions = LockActions;
exports.LockTamperedAction = LockTamperedAction;
exports.LockTamperedRawAction = LockTamperedRawAction;
exports.LockStatuses = LockStatuses;
exports.LockDoorStateStatuses = LockDoorStateStatuses;
exports.LockReservedSlots = LockReservedSlots;
exports.LockRawStatuses = LockRawStatuses;
exports.LockDoorStateRawStatuses = LockDoorStateRawStatuses;
exports.LockBatteryWarningStates = LockBatteryWarningStates;
exports.LockBatteryAlgorithmNames = LockBatteryAlgorithmNames;
exports.LockBatteryLifeEstimatesInMS = LockBatteryLifeEstimatesInMS;
exports.LOCK_TYPE_1001_DEFAULT_YALE = '1.2.3';
exports.LOCK_TYPE_1001_DEFAULT_DLG = '1.3.13';
exports.LOCK_TYPE_1002_DEFAULT_YALE = '1.2.3';
exports.LOCK_TYPE_1002_DEFAULT_NORDIC = '1.2.3';
exports.LOCK_TYPE_1003_DEFAULT_YALE = '1.2.3';
exports.LOCK_TYPE_1003_DEFAULT_DLG = '1.2.3';
exports.LOCK_TYPE_4_DEFAULT_DLG = '1.3.13';
exports.LOCK_TYPE_3_DEFAULT_DLG = '1.3.13';
exports.LOCK_TYPE_3_DEFAULT_SD = '1.3.13';
exports.LOCK_TYPE_2_DEFAULT_BCM = '1.2.3';
exports.LOCK_TYPE_2_DEFAULT_ST = '1.2.3';
exports.LOCK_TYPE_1_DEFAULT_TI = '1.2.3';
exports.LOCK_TYPE_1_DEFAULT_ARM = '1.2.3';
exports.LOCK_TYPE_1_DEFAULT_VERSION = '1a2a3a4a';
exports.DOOR_STATE_OPEN_TIMEOUT = DOOR_STATE_OPEN_TIMEOUT;
exports.DOOR_STATE_LOG_USER_ID = DOOR_STATE_LOG_USER_ID;
exports.DOOR_STATE_LOG_USER_FIRST_NAME = DOOR_STATE_LOG_USER_FIRST_NAME;
exports.DOOR_STATE_LOG_USER_LAST_NAME = DOOR_STATE_LOG_USER_LAST_NAME;
exports.DOOR_STATE_UPDATE_NO_LOG_SCORE = DOOR_STATE_UPDATE_NO_LOG_SCORE;
exports.HOMEKIT_LOCK_TYPES = HOMEKIT_LOCK_TYPES;
exports.LockCredentialActions = LockCredentialActions;
